import { c as _c } from "react/compiler-runtime";
import { centsToUnits } from '@systemeio/ui';
import LazyContactInfoTooltip from 'modules/contacts/components/lazy-contact-info-tooltip';
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url';
import { MainEventsEnum, mainEventsDescription, mainEventsType } from 'modules/main/enums/main-events-enum';
import { MainEventType } from 'modules/main/types/main-interface';
import React, { type JSX } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url';
import { MoneyCancelIcon, MoneyReturnIcon, MoneySucceedIcon, PersonIcon } from '@systemeio/ui/icons';
interface RenderEventProps {
  event: MainEventType;
  lastItem?: boolean;
}
const eventIcon: Record<MainEventsEnum, JSX.Element> = {
  [MainEventsEnum.contact_created]: <PersonIcon className={'h-[24px] w-[24px] fill-[#35E190]'} />,
  [MainEventsEnum.recurring_charge_succeed]: <MoneyReturnIcon />,
  [MainEventsEnum.one_shot_charge_succeed]: <MoneySucceedIcon />,
  [MainEventsEnum.payment_subscription_stopped]: <MoneyCancelIcon />,
  [MainEventsEnum.payment_subscription_completed]: <MoneyReturnIcon />,
  [MainEventsEnum.payment_subscription_created]: <MoneyReturnIcon />,
  [MainEventsEnum.recurring_charge_failed]: <MoneyCancelIcon />
};
function RenderEvent(t0) {
  const $ = _c(38);
  const {
    event,
    lastItem
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  let t1;
  if ($[0] !== event.relatedObject || $[1] !== event.type || $[2] !== t || $[3] !== user?.dashboardLocale) {
    t1 = () => {
      switch (event.type) {
        case MainEventsEnum.recurring_charge_succeed:
        case MainEventsEnum.one_shot_charge_succeed:
          {
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              price: currencyFormat(centsToUnits(event.relatedObject.amount), event.relatedObject.payment.currency, user?.dashboardLocale),
              offer_name: event.relatedObject.orderItem.pricePlan ? event.relatedObject.orderItem.pricePlan.innerName : event.relatedObject.orderItem.productOrderItem ? event.relatedObject.orderItem.productOrderItem.product.name : "",
              email: event.relatedObject.orderItem.order.customer.contact.email
            }} components={{
              contactLink: <LazyContactInfoTooltip contactId={event.relatedObject.orderItem.order.customer.contact.id} removedAt={event.relatedObject.orderItem.order.customer.contact.removedAt} />,
              a: event.relatedObject.orderItem.offer && event.relatedObject.orderItem.offer.funnelStep ? <LinkWithoutPrefetch className="hover:text-text-tertiary/70 truncate underline" href={getFunnelStepUrl(event.relatedObject.orderItem.offer.funnelStep.funnel.id, event.relatedObject.orderItem.offer.funnelStep.id)} /> : <span />
            }} />;
          }
        case MainEventsEnum.payment_subscription_created:
          {
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              customer_name: event.relatedObject.orderItem.order.customer.fullName || event.relatedObject.orderItem.order.customer.contact.connectedUser?.fullName || event.relatedObject.orderItem.order.customer.contact.email,
              offer_name: event.relatedObject.orderItem.pricePlan ? event.relatedObject.orderItem.pricePlan.innerName : event.relatedObject.orderItem.productOrderItem ? event.relatedObject.orderItem.productOrderItem.product.name : ""
            }} components={{
              a: <LazyContactInfoTooltip contactId={event.relatedObject.orderItem.order.customer.contact.id} removedAt={event.relatedObject.orderItem.order.customer.contact.removedAt} />,
              b: event.relatedObject.orderItem.offer && event.relatedObject.orderItem.offer.funnelStep ? <LinkWithoutPrefetch className="hover:text-text-tertiary/70 truncate underline" href={getFunnelStepUrl(event.relatedObject.orderItem.offer.funnelStep.funnel.id, event.relatedObject.orderItem.offer.funnelStep.id)} /> : <span />
            }} />;
          }
        case MainEventsEnum.recurring_charge_failed:
        case MainEventsEnum.payment_subscription_stopped:
        case MainEventsEnum.payment_subscription_completed:
          {
            return <TransLoco t={t} i18nKey={mainEventsDescription[event.type]} values={{
              customer_name: event.relatedObject.orderItem.order.customer.fullName || event.relatedObject.orderItem.order.customer.contact.connectedUser?.fullName || event.relatedObject.orderItem.order.customer.contact.email,
              offer_name: event.relatedObject.orderItem.pricePlan ? event.relatedObject.orderItem.pricePlan.innerName : event.relatedObject.orderItem.productOrderItem ? event.relatedObject.orderItem.productOrderItem.product.name : ""
            }} components={{
              a: <LazyContactInfoTooltip contactId={event.relatedObject.orderItem.order.customer.contact.id} removedAt={event.relatedObject.orderItem.order.customer.contact.removedAt} />,
              b: event.relatedObject.orderItem.offer && event.relatedObject.orderItem.offer.funnelStep ? <LinkWithoutPrefetch className="hover:text-text-tertiary/70 truncate underline" href={getFunnelStepUrl(event.relatedObject.orderItem.offer.funnelStep.funnel.id, event.relatedObject.orderItem.offer.funnelStep.id)} /> : <span />
            }} />;
          }
        case MainEventsEnum.contact_created:
          {
            const funnelStep = event.relatedObject.sourcePage?.funnelStep && <LinkWithoutPrefetch className="hover:text-text-tertiary/70 underline" href={getFunnelStepUrl(event.relatedObject.sourcePage.funnelStep.funnel.id, event.relatedObject.sourcePage.funnelStep.id)} />;
            const blogPage = event.relatedObject.sourcePage?.blogPage && <LinkWithoutPrefetch className="hover:text-text-tertiary/70 underline" href={getBlogPostNextjsUrl(event.relatedObject.sourcePage.blogPage.blog.id)} />;
            return <TransLoco t={t} i18nKey={mainEventsDescription[MainEventsEnum.contact_created]} values={{
              email: event.relatedObject.email,
              form: event.relatedObject.pageTitle
            }} components={{
              a: <LazyContactInfoTooltip contactId={event.relatedObject.id} removedAt={event.relatedObject.removedAt} />,
              b: event.relatedObject.sourcePage ? funnelStep || blogPage || <span /> : <span />
            }} />;
          }
      }
    };
    $[0] = event.relatedObject;
    $[1] = event.type;
    $[2] = t;
    $[3] = user?.dashboardLocale;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getEventElement = t1;
  const t2 = `${lastItem ? "pt-4" : "py-[15px]"} font-averta flex gap-2.5`;
  const t3 = eventIcon[event.type];
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="[&>svg]:scale-75 flex h-[24px] min-w-[28px] items-center justify-center rounded-full">{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = mainEventsType[event.type];
  let t6;
  if ($[7] !== t || $[8] !== t5) {
    t6 = t(t5);
    $[7] = t;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <span className="font-bold text-[#414355]">{t6}</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = user?.timeFormat;
  const t9 = user?.dateFormat;
  const t10 = user?.timezone;
  let t11;
  if ($[12] !== event.createdAt || $[13] !== t10 || $[14] !== t8 || $[15] !== t9) {
    t11 = dateTimeFormat(event.createdAt, {
      timeFormat: t8,
      dateFormat: t9,
      timeZone: t10
    });
    $[12] = event.createdAt;
    $[13] = t10;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11) {
    t12 = <span className="text-[rgb(var(--text-secondary))] truncate text-xs">{t11}</span>;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t7) {
    t13 = <div className="flex items-center justify-between gap-3">{t7}{t12}</div>;
    $[19] = t12;
    $[20] = t7;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== getEventElement) {
    t14 = getEventElement();
    $[22] = getEventElement;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14) {
    t15 = <div className="flex flex-wrap gap-1 truncate text-xs leading-normal">{t14}</div>;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13 || $[27] !== t15) {
    t16 = <div className="flex w-full flex-col gap-1 truncate">{t13}{t15}</div>;
    $[26] = t13;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16 || $[30] !== t2 || $[31] !== t4) {
    t17 = <div className={t2}>{t4}{t16}</div>;
    $[29] = t16;
    $[30] = t2;
    $[31] = t4;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== lastItem) {
    t18 = !lastItem && <hr className="color-text-primary opacity-10" />;
    $[33] = lastItem;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t17 || $[36] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
}
export default RenderEvent;